import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import FundraisingHeader from "../layout/header";
import FundraisingFooter from '../layout/footer';

const WaysToGive = () => {
    const location = useLocation();
    // Data for the list of giving options
    const givingOptions = [
        {
            title: 'Make a Gift',
            desc: 'Make an immediate, one-time donation to provide essential resources, educational materials, and direct support to children and families in need today.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 7h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V9c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V9h5v2h2V9h4v2h2V9h3v5z" />
                </svg>
            )
        },
        {
            title: 'Monthly Giving',
            desc: 'Become a recurring donor. Your consistent monthly support allows us to plan long-term programs, expand our reach, and sustain our mission year-round.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                    <path d="M9 14H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
                </svg>
            )
        },
        {
            title: 'Pledge a Gift',
            desc: 'Commit to a future donation over a set period. Pledges help us confidently budget for upcoming facility expansions and new therapeutic programs.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
            )
        },
        {
            title: 'Donate Stock or Assets',
            desc: 'Gift appreciated securities, real estate, or other assets. This tax-smart way of giving can provide significant support to our foundation while offering financial benefits to you.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M10 20h4V4h-4v16zm-6 0h4v-8H4v8zM16 9v11h4V9h-4z" />
                </svg>
            )
        },
        {
            title: 'Leave a Legacy',
            desc: 'Include The Just Generation Foundation in your will or estate plan. Your lasting legacy will ensure future generations have access to inclusive education and care.',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                </svg>
            )
        }
    ];

    useEffect(() => {
        if (location.hash === '#donate') {
            // Wait a tick so the DOM (and Givebutter widget) is rendered
            const timer = setTimeout(() => {
                const el = document.getElementById('donate');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300); // 300ms — increase if the widget loads slowly

            return () => clearTimeout(timer);
        }
    }, [location]);

    return (
        <>
            {/* Fix: Add scroll margin top so sticky header doesn't cover the cards */}
            <style>
                {`
                    .scroll-target {
                        scroll-margin-top: 160px; /* Adjust this value based on your header height */
                    }
                `}
            </style>

            <FundraisingHeader />
            <main className="overflow-hidden" style={{ marginTop: "-20px" }}>

                {/* ================= SPLIT HERO SECTION ================= */}
                <section className="position-relative">
                    <div className="container-fluid px-0">
                        <div className="row g-0 d-flex align-items-stretch">

                            {/* LEFT COLUMN - Glassmorphism Panel */}
                            <div
                                className="col-lg-5 p-4 p-md-5 d-flex flex-column justify-content-center position-relative"
                                style={{
                                    backgroundColor: 'rgba(253, 250, 244, 0.88)',
                                    backdropFilter: 'blur(16px)',
                                    WebkitBackdropFilter: 'blur(16px)',
                                    minHeight: '85vh',
                                    zIndex: 2
                                }}
                            >
                                <div className="ps-lg-4 py-4" style={{ maxWidth: '500px' }}>
                                    <h1
                                        className="fw-bold mb-3"
                                        style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', lineHeight: 1.05, color: '#1e293b' }}
                                    >
                                        Ways to <span style={{ color: '#d97706' }}>Give.</span>
                                    </h1>

                                    <p className="lh-lg mb-4" style={{ color: '#4b5563', fontSize: '1.05rem' }}>
                                        There are many ways to make a difference in the lives of children and families.
                                    </p>

                                    <div className="d-flex flex-column gap-3 mt-2">
                                        {givingOptions.map((option, idx) => (
                                            <a href={`#option-${idx}`} key={idx} className="text-decoration-none">
                                                <div
                                                    className="d-flex align-items-center gap-3 px-4 py-3 rounded-3"
                                                    style={{
                                                        boxShadow: '0 8px 20px -10px rgba(15,23,42,0.08)',
                                                        border: '1px solid rgba(0,0,0,0.02)',
                                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                                        backgroundColor: '#1e293b',
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(5px)'; e.currentTarget.style.boxShadow = '0 12px 25px -8px rgba(15,23,42,0.15)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.boxShadow = '0 8px 20px -10px rgba(15,23,42,0.08)'; }}
                                                >
                                                    <span style={{ color: '#ffffff' }}>{option.icon}</span>
                                                    <span className="fw-bold" style={{ color: '#ffffff', fontSize: '1.05rem' }}>{option.title}</span>
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT COLUMN - Full Height Background Image */}
                            <div className="col-lg-7 position-relative d-none d-lg-block p-0">
                                <div className="w-100 h-100 position-relative">
                                    <img
                                        src="/assets/images/fundraising/03_ways_to_give_background.jpg"
                                        alt="Happy child playing"
                                        className="w-100 h-100 position-absolute top-0 start-0"
                                        style={{ objectFit: 'cover', objectPosition: 'center' }}
                                    />
                                    <div
                                        className="position-absolute top-0 start-0 w-100 h-100"
                                        style={{ background: 'linear-gradient(90deg, rgba(253, 250, 244, 0.98) 0%, rgba(253, 250, 244, 0.6) 15%, rgba(253, 250, 244, 0) 40%)' }}
                                    ></div>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {/* ================= NEW SECTION: EXPLORE IMPACT ================= */}
                <section className="py-5" style={{ background: '#fdfaf4', paddingTop: '6rem', paddingBottom: '6rem' }}>
                    <div className="container">

                        {/* Section Header */}
                        <div className="row justify-content-center text-center mb-5">
                            <div className="col-lg-8">
                                <span
                                    className="d-inline-flex align-items-center gap-2 rounded-pill fw-semibold mb-3"
                                    style={{ background: 'rgba(251,191,36,0.12)', color: '#b45309', padding: '0.4rem 0.9rem', fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', border: '1px solid rgba(251,191,36,0.35)' }}
                                >
                                    <span className="d-inline-block rounded-circle" style={{ width: 6, height: 6, background: '#f59e0b' }}></span>
                                    Explore Your Impact
                                </span>
                                <h2
                                    className="fw-bold mb-3"
                                    style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', color: '#0f172a', lineHeight: 1.15 }}
                                >
                                    Choose How You Make a <span style={{ color: '#d97706' }}>Difference</span>
                                </h2>
                                <p className="lh-lg mx-auto" style={{ color: '#4b5563', fontSize: '1.05rem', maxWidth: '600px' }}>
                                    Every contribution, no matter the size, directly supports our mission to build a brighter, more inclusive future.
                                </p>
                            </div>
                        </div>

                        {/* Cards Grid */}
                        <div className="row g-4 justify-content-center">
                            {givingOptions.map((option, idx) => (
                                <div className="col-lg-4 col-md-6 scroll-target" key={idx} id={`option-${idx}`}>
                                    <div
                                        className="card h-100 border-0 rounded-4 p-4"
                                        style={{
                                            background: '#ffffff',
                                            boxShadow: '0 15px 35px -20px rgba(15,23,42,0.08)',
                                            transition: 'all 0.3s ease',
                                            border: '1px solid rgba(0,0,0,0.03)',
                                            cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.02)';
                                            e.currentTarget.style.boxShadow = '0 25px 45px -10px rgba(15,23,42,0.2)';
                                            e.currentTarget.style.background = '#1e293b';

                                            e.currentTarget.querySelector('h4').style.color = '#ffffff';
                                            e.currentTarget.querySelector('p').style.color = 'rgba(255,255,255,0.7)';

                                            const iconWrap = e.currentTarget.querySelector('.icon-wrapper');
                                            iconWrap.style.background = 'rgba(255,255,255,0.1)';
                                            iconWrap.style.color = '#fbbf24';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                            e.currentTarget.style.boxShadow = '0 15px 35px -20px rgba(15,23,42,0.08)';
                                            e.currentTarget.style.background = '#ffffff';

                                            e.currentTarget.querySelector('h4').style.color = '#1e293b';
                                            e.currentTarget.querySelector('p').style.color = '#64748b';

                                            const iconWrap = e.currentTarget.querySelector('.icon-wrapper');
                                            iconWrap.style.background = 'rgba(251,191,36,0.15)';
                                            iconWrap.style.color = '#d97706';
                                        }}
                                    >
                                        <div className="card-body d-flex flex-column p-0">
                                            <div
                                                className="icon-wrapper d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                                                style={{ width: 56, height: 56, background: 'rgba(251,191,36,0.15)', color: '#d97706', transition: 'all 0.3s ease' }}
                                            >
                                                {option.icon}
                                            </div>

                                            <h4 className="fw-bold mb-3" style={{ color: '#1e293b', fontFamily: '"Playfair Display", serif', fontSize: '1.35rem', transition: 'color 0.3s ease' }}>
                                                {option.title}
                                            </h4>

                                            <p className="lh-lg mb-0 flex-grow-1" style={{ color: '#64748b', fontSize: '0.95rem', transition: 'color 0.3s ease' }}>
                                                {option.desc}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Bottom CTA Button */}
                        <div className="row mt-5 pt-3">
                            <div className="col-12 text-center">
                                <Link to="/campaign-contact" className="text-decoration-none">
                                    <button
                                        className="btn fw-bold rounded-pill px-5 py-3 border-0"
                                        style={{
                                            background: '#1e293b',
                                            color: '#ffffff',
                                            boxShadow: '0 10px 25px -5px rgba(30,41,59,0.3)',
                                            transition: 'transform 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                                    >
                                        Contact Us to Discuss Giving Options
                                    </button>
                                </Link>
                            </div>
                        </div>

                    </div>
                </section>

                {/* ================= NEW: GIVEBUTTER DONATION SECTION ================= */}
                <section id="donate" className="py-5 scroll-target" style={{ background: '#fdfaf4', paddingTop: '2rem', paddingBottom: '6rem' }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-9">

                                <div
                                    className="card border-0 rounded-4 shadow-lg p-4 p-md-5 text-center"
                                    style={{ background: '#ffffff', borderTop: '4px solid #fbbf24 !important' }}
                                >
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mx-auto mb-4" style={{ width: 64, height: 64, background: 'rgba(251,191,36,0.15)', color: '#d97706' }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                        </svg>
                                    </div>

                                    <h2
                                        className="fw-bold mb-3"
                                        style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 2.5rem)', color: '#0f172a' }}
                                    >
                                        Make Your Donation
                                    </h2>
                                    <p className="mb-5 mx-auto" style={{ color: '#4b5563', fontSize: '1.05rem', maxWidth: '600px' }}>
                                        Ready to make an impact? Use our secure donation form below to contribute directly to our mission.
                                    </p>

                                    {/* Givebutter Widget Container */}
                                    {/* <div className="d-flex justify-content-center w-100 overflow-hidden rounded-4">
                                        <givebutter-widget id="gJRrA7"></givebutter-widget>
                                    </div> */}
<div className="d-flex justify-content-center align-items-center w-100 overflow-hidden rounded-4">
    <givebutter-widget
        id="gJRrA7"
        align="center"
        style={{
            display: "block",
            width: "100%",
            maxWidth: "600px"
        }}
    ></givebutter-widget>
</div>

                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ================= DETAILS / IMPACT SECTION ================= */}
                <section className="py-5" style={{ background: '#1e293b', paddingTop: '6rem', paddingBottom: '6rem' }}>
                    <div className="container text-center">
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <span
                                    className="d-inline-flex align-items-center gap-2 rounded-pill fw-semibold mb-4"
                                    style={{ background: 'rgba(251,191,36,0.15)', color: '#fcd34d', padding: '0.4rem 1rem', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', border: '1px solid rgba(251,191,36,0.35)' }}
                                >
                                    Your Support Matters
                                </span>
                                <h2
                                    className="fw-bold mb-4 text-white"
                                    style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', lineHeight: 1.15 }}
                                >
                                    Every contribution represents an opportunity to <span style={{ color: '#fbbf24' }}>make a positive impact.</span>
                                </h2>
                                <p className="lh-lg text-white-50 mb-0" style={{ fontSize: '1.1rem' }}>
                                    Your generosity can help create meaningful opportunities and build a brighter future for children and families. At The Just Generation Foundation, we welcome support from individuals, families, businesses, and community organizations who share our vision for positive change. Together, we can build a supportive network that empowers families, connects resources, and creates lasting change for future generations.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ================= NEW: MAKE A PLEDGE TODAY ================= */}
                <section className="py-5" style={{ background: '#fdfaf4', paddingTop: '6rem', paddingBottom: '6rem' }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-8 text-center">
                                <div
                                    className="card border-0 rounded-4 shadow-lg p-4 p-md-5"
                                    style={{ background: '#ffffff', borderTop: '4px solid #fbbf24 !important' }}
                                >
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mx-auto mb-4" style={{ width: 64, height: 64, background: 'rgba(251,191,36,0.15)', color: '#d97706' }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>

                                    <h3
                                        className="fw-bold mb-3"
                                        style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 'clamp(1.8rem, 3vw, 2.25rem)', color: '#0f172a' }}
                                    >
                                        Ready to Make a Pledge?
                                    </h3>
                                    <p className="mb-4 mx-auto" style={{ color: '#4b5563', fontSize: '1.05rem', maxWidth: '500px' }}>
                                        Whether you are considering a one-time gift or a long-term legacy, our team is here to help you navigate the process.
                                    </p>

                                    <Link to="/campaign-contact" className="text-decoration-none">
                                        <button
                                            className="btn fw-bold rounded-pill px-5 py-3 border-0"
                                            style={{
                                                background: '#1e293b',
                                                color: '#ffffff',
                                                boxShadow: '0 10px 25px -5px rgba(30,41,59,0.3)',
                                                transition: 'transform 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                                            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                                        >
                                            Contact Our Team
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            </main>
            <FundraisingFooter />
        </>
    );
};

export default WaysToGive;